/**
 * Axis-aligned camera views — Blender Numpad 1/3/7 (and their ctrl-flipped
 * counterparts) ported to our Z-up world.
 *
 * When a view is activated, the camera orbits to the canonical axis-aligned
 * position around the current `cameraTarget`, preserving the existing orbit
 * distance, and the projection flips to orthographic. The pivot (target)
 * stays where it was, matching Blender's default behavior.
 *
 * Break-out is owned by the authoritative camera mutation paths that write
 * camera state. If a write steers the view direction off the locked axis, the
 * lock is exited immediately in that same path and the previous projection /
 * focal length are restored while keeping the new camera position.
 */

import { getSelectionWorldCenter, useStore } from "../app/store.ts";
import type { ViewAxis, ViewSign } from "../app/storeTypes.ts";
import { flushCameraSync } from "./cameraSync.ts";
import {
  lockedOrthographicHalfHeight,
  viewPosition,
  viewUp,
} from "./viewLock.ts";

/**
 * Snap the camera to an axis-aligned view. Preserves the orbit pivot and
 * distance (Blender convention) and switches to orthographic. Pose is
 * applied instantly — an earlier animated version was glitchy under rapid
 * retriggers and under the async projection swap, and jumping straight
 * to the final pose removes that whole class of failure.
 *
 * Reads the live camera pose from OrbitControls so a snap triggered during
 * an in-flight drag or wheel gesture uses the user's actual current view,
 * not the store's stale snapshot (which is only rewritten at orbit-end).
 */
export function snapToAxisView(axis: ViewAxis, sign: ViewSign): void {
  const s = useStore.getState();
  const controls = window.__poseref_orbitControls?.current;
  const livePos = controls?.object.position;
  const liveTgt = controls?.target;

  // Pivot around the current selection when one exists (Blender's "Orbit
  // Around Selection" default). Without this, axis-snapping while an
  // off-origin object is selected jumps the camera to the world-origin
  // pivot, which is jarring — the object the user is working on flies
  // off-screen. Falls back to the live orbit target when nothing is
  // selected, preserving the free-camera case.
  const selectionCenter = getSelectionWorldCenter(s);
  const target: [number, number, number] =
    selectionCenter ??
    (liveTgt ? [liveTgt.x, liveTgt.y, liveTgt.z] : s.cameraTarget);
  const pos: [number, number, number] = livePos
    ? [livePos.x, livePos.y, livePos.z]
    : s.cameraPosition;
  const dx = pos[0] - target[0];
  const dy = pos[1] - target[1];
  const dz = pos[2] - target[2];
  const dist = Math.max(0.01, Math.sqrt(dx * dx + dy * dy + dz * dz));

  const toPos = viewPosition(axis, sign, target, dist);
  const up = viewUp(axis, sign);
  const halfHeight = lockedOrthographicHalfHeight(
    dist,
    s.focalLength,
    s.cameraProjection,
    s.orthographicHalfHeight,
    s.viewLock != null,
  );

  window.__poseref_cameraController?.applyState({
    projection: "orthographic",
    position: toPos,
    target,
    up,
    orthographicHalfHeight: halfHeight,
    focalLength: s.focalLength,
  });
  s.enterViewLock(axis, sign, dist, toPos, target, up);
}

/**
 * Explicitly exit the current axis-view lock. If the pre-lock projection was
 * perspective, reposition the camera so the apparent scale of the ortho view
 * carries over: the new distance is `orthographicHalfHeight / tan(fov/2)`,
 * preserving the half-screen vertical extent at the pivot. If the pre-lock
 * projection was already ortho we just flip the lock off — the new ortho
 * half-height is restored by `exitViewLock` itself.
 *
 * Called for "manual" exits (user tapped the active view or selected
 * "Perspective" in the pie). Rotation break-outs are handled by the
 * authoritative camera mutation paths that own the new pose.
 */
export function exitAxisView(): void {
  const s = useStore.getState();
  const lock = s.viewLock;
  if (!lock) return;

  if (lock.prevProjection === "perspective") {
    const controls = window.__poseref_orbitControls?.current;
    const pos = controls?.object.position ?? {
      x: s.cameraPosition[0],
      y: s.cameraPosition[1],
      z: s.cameraPosition[2],
    };
    const tgt = controls?.target ?? {
      x: s.cameraTarget[0],
      y: s.cameraTarget[1],
      z: s.cameraTarget[2],
    };
    const dx = pos.x - tgt.x,
      dy = pos.y - tgt.y,
      dz = pos.z - tgt.z;
    const len = Math.hypot(dx, dy, dz);

    // Compute perspective distance that reproduces the current ortho framing:
    // dist = orthographicHalfHeight / tan(fov/2). 24mm sensor → 12mm half.
    const fovRad = 2 * Math.atan(12 / lock.prevFocalLength);
    const prevTan = Math.tan(fovRad / 2);
    const newDist =
      prevTan > 1e-6 && len > 1e-6
        ? Math.max(0.01, s.orthographicHalfHeight / prevTan)
        : len;
    const scale = len > 1e-6 ? newDist / len : 1;
    const target: [number, number, number] = [tgt.x, tgt.y, tgt.z];
    const toPos: [number, number, number] = [
      tgt.x + dx * scale,
      tgt.y + dy * scale,
      tgt.z + dz * scale,
    ];

    window.__poseref_cameraController?.applyState({
      projection: "perspective",
      position: toPos,
      target,
      up: null,
      focalLength: lock.prevFocalLength,
    });
    s.exitViewLock("manual");
    // Sync the just-applied live pose to the store in a single atomic write.
    flushCameraSync();
  } else {
    window.__poseref_cameraController?.applyState({
      projection: "orthographic",
      orthographicHalfHeight:
        lock.prevOrthographicHalfHeight ?? s.orthographicHalfHeight,
      up: s.cameraUp,
    });
    s.exitViewLock("manual");
    flushCameraSync();
  }
}

/**
 * Toggle an axis-aligned view. If the camera is already locked to the same
 * axis + sign, exit the lock (restoring the pre-lock projection + focal
 * length). Otherwise snap to the requested view. Used by both the keymap
 * shortcuts (Alt+1..6) and the tool-palette belt so both surfaces share
 * identical toggle semantics.
 */
export function toggleAxisView(axis: ViewAxis, sign: ViewSign): void {
  const lock = useStore.getState().viewLock;
  if (lock && lock.axis === axis && lock.sign === sign) {
    exitAxisView();
  } else {
    snapToAxisView(axis, sign);
  }
}

/** All six axis views, ordered as they appear in the picker belt. */
export const VIEW_AXIS_OPTIONS: ReadonlyArray<{
  axis: ViewAxis;
  sign: ViewSign;
  label: string;
}> = [
  { axis: "y", sign: -1, label: "Front" },
  { axis: "y", sign: 1, label: "Back" },
  { axis: "x", sign: -1, label: "Left" },
  { axis: "x", sign: 1, label: "Right" },
  { axis: "z", sign: 1, label: "Top" },
  { axis: "z", sign: -1, label: "Bottom" },
];
